// Sparkline geometry. Extracted so more than one surface can draw the same shape.
//
// src/components/market/MarketStrip.tsx has an equivalent private helper that predates this file;
// it is left alone deliberately (it feeds the live IG strip on the partner dashboard and the mobile
// markets tab) but should adopt this one next time it is touched.

/**
 * Build the `points` attribute for an SVG <polyline> from a series of values.
 *
 * Returns "" for fewer than two points, which renders nothing rather than a misleading flat line.
 * A completely flat series is drawn along the vertical middle rather than the floor, so it does not
 * read as "at the bottom of its range".
 */
export function sparklinePoints(values: number[], width: number, height: number, pad = 1): string {
  const clean = values.filter((v) => Number.isFinite(v));
  if (clean.length < 2) return "";

  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const span = max - min;
  const usable = Math.max(height - pad * 2, 1);
  const stepX = width / (clean.length - 1);

  return clean
    .map((v, i) => {
      const y = span === 0 ? height / 2 : pad + (1 - (v - min) / span) * usable;
      return `${(i * stepX).toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

/**
 * Where `value` sits between `min` and `max`, as a 0-1 fraction, clamped.
 * Used to place the marker on a range track. Returns 0.5 for a degenerate range.
 */
export function rangeFraction(value: number, min: number, max: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(max)) return 0.5;
  if (max === min) return 0.5;
  return Math.min(1, Math.max(0, (value - min) / (max - min)));
}
